import React, {useCallback, useMemo} from 'react';
import styled from 'styled-components';
import {SubNavigation} from '@/web-components';
import Util from '../../../../forecast-app/shared/util/util';
import {closeModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import CreateNovaAgentMutation from '../../../../mutations/nova/CreateNovaAgentMutation';
import UpdateNovaAgentMutation from '../../../../mutations/nova/UpdateNovaAgentMutation';
import CreateNovaAgentCategoryMutation from '../../../../mutations/nova/CreateNovaAgentCategoryMutation';
import {SaveState} from '../shared/saveState';
import {AgentFormValues} from '../shared/novaAgentForm';
import {toRelayGlobalId} from '../shared/relayGlobalId';
import {showCreateAgentModal, showEditAgentModal} from '../modals/showCreateAgentModal';
import {NovaStudioHeader} from './NovaStudioHeader';
import {NovaStudioCatalogue} from './NovaStudioCatalogue';
import {StudioAgentEntry, StudioCategoryEntry, StudioTab, useNovaStudioShell} from './useNovaStudioShell';

const Shell = styled.div`
	display: flex;
	flex-direction: row;
	flex-grow: 1;
	min-height: 0;
`;
const MainColumn = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-width: 0;
`;
const TabContent = styled.div`
	flex-grow: 1;
	padding: 24px;
`;

// Placeholder copy — not localised yet; must go through the translations workflow.
const TAB_LABELS: Record<StudioTab, string> = {
	[StudioTab.Prompt]: 'Prompt',
	[StudioTab.AgentMd]: 'agent.md',
	[StudioTab.Deploy]: 'Deploy',
};
const TAB_ORDER: StudioTab[] = [StudioTab.Prompt, StudioTab.AgentMd, StudioTab.Deploy];

interface Props {
	categories: StudioCategoryEntry[];
	agents: StudioAgentEntry[];
	saveState?: SaveState;
	// `CreateNovaAgentCategoryMutation`/`UpdateNovaAgentMutation` have no `@connection`-backed way
	// to add a brand-new category or move an agent between categories into the Relay store (see
	// their comments) — the caller (NovaStudioPage) wires this to `ForecastQueryRenderer`'s own
	// `retry` so those cases fall back to a full catalogue refetch instead of a bespoke updater.
	onRefetchCatalogue?: () => void;
}

export function NovaStudioShell({categories, agents, saveState = SaveState.Saved, onRefetchCatalogue}: Props): JSX.Element {
	const shell = useNovaStudioShell({categories, agents, saveState});

	const categoryOptions = useMemo(() => categories.map(({categoryId, name}) => ({categoryId, name})), [categories]);

	const handleCreateCategory = useCallback(
		(name: string): Promise<{categoryId: string; name: string}> =>
			new Promise((resolve, reject) => {
				Util.CommitMutation(
					CreateNovaAgentCategoryMutation,
					{name},
					response => {
						const category = response.createNovaAgentCategory?.category;
						if (!category) {
							reject(new Error('createNovaAgentCategory returned no category'));
							return;
						}
						// No `@connection` to insert into on this plain list (see the mutation's
						// own comment) — refetch so the new category shows up in the catalogue.
						onRefetchCatalogue?.();
						resolve({categoryId: Util.getUUIdFromBase64String(category.id), name: category.name ?? name});
					},
					true,
					null,
					(transaction, onFailure) => {
						onFailure(transaction);
						reject(new Error('createNovaAgentCategory mutation failed'));
					}
				);
			}),
		[onRefetchCatalogue]
	);

	const handleCreateSubmit = useCallback(
		(values: AgentFormValues): Promise<void> =>
			new Promise((resolve, reject) => {
				if (!values.categoryId) {
					reject(new Error('createNovaAgent requires a categoryId'));
					return;
				}
				Util.CommitMutation(
					CreateNovaAgentMutation,
					{
						name: values.name,
						categoryId: values.categoryId,
						title: values.title,
						description: values.description,
						shape: values.shape,
						categoryConnectionParentId: toRelayGlobalId('NovaAgentCategory', values.categoryId),
					},
					response => {
						const newAgentGlobalId = response.createNovaAgent?.agent?.node?.id;
						closeModal();
						if (newAgentGlobalId) {
							// The mutation's RANGE_ADD already appended the edge, so the catalogue
							// updates itself; select the new agent (which resets to the Prompt tab).
							shell.selectAgent(Util.getUUIdFromBase64String(newAgentGlobalId));
						}
						resolve();
					},
					true,
					null,
					(transaction, onFailure) => {
						// Call the fallback so the standard error modal still fires, and reject
						// so the modal's own inline failure state renders too.
						onFailure(transaction);
						reject(new Error('createNovaAgent mutation failed'));
					}
				);
			}),
		[shell]
	);

	const openCreateModal = useCallback((): void => {
		showCreateAgentModal({
			mode: 'create',
			categories: categoryOptions,
			existingNames: agents.map(agent => agent.name),
			onSubmit: handleCreateSubmit,
			onCreateCategory: handleCreateCategory,
		});
	}, [categoryOptions, agents, handleCreateSubmit, handleCreateCategory]);

	const openEditModal = useCallback(
		(agentId: string): void => {
			const agent = agents.find(candidate => candidate.agentId === agentId);
			if (!agent) {
				return;
			}
			const handleEditSubmit = (values: AgentFormValues): Promise<void> =>
				new Promise((resolve, reject) => {
					if (!values.categoryId) {
						reject(new Error('updateNovaAgent requires a categoryId'));
						return;
					}
					Util.CommitMutation(
						UpdateNovaAgentMutation,
						{
							id: toRelayGlobalId('NovaAgent', agent.agentId),
							name: values.name,
							categoryId: values.categoryId,
							title: values.title,
							description: values.description,
							shape: values.shape,
						},
						() => {
							closeModal();
							// The agent's connection membership is keyed off its category on the
							// server; Relay merges the updated node by id but can't move it between
							// connections on its own, so refetch when the category actually changed
							// (see UpdateNovaAgentMutation.ts).
							if (values.categoryId !== agent.categoryId) {
								onRefetchCatalogue?.();
							}
							resolve();
						},
						true,
						null,
						(transaction, onFailure) => {
							// Call the fallback so the standard error modal still fires, and reject
							// so the modal's own inline failure state renders too.
							onFailure(transaction);
							reject(new Error('updateNovaAgent mutation failed'));
						}
					);
				});
			showEditAgentModal({
				categories: categoryOptions,
				existingNames: agents.filter(candidate => candidate.agentId !== agentId).map(candidate => candidate.name),
				initialValues: {
					name: agent.name,
					categoryId: agent.categoryId,
					title: agent.title,
					description: agent.description,
					shape: agent.shape,
				},
				onSubmit: handleEditSubmit,
				onCreateCategory: handleCreateCategory,
			});
		},
		[agents, categoryOptions, handleCreateCategory, onRefetchCatalogue]
	);

	return (
		<Shell data-cy="nova-studio-shell">
			<NovaStudioCatalogue
				catalogue={shell.catalogue}
				selectedAgentId={shell.selectedAgentId}
				onSelectAgent={shell.selectAgent}
				onCreateAgent={openCreateModal}
				onEditAgent={openEditModal}
			/>
			<MainColumn>
				<NovaStudioHeader agentName={shell.selectedAgent?.name ?? null} saveState={shell.saveState} />
				<SubNavigation>
					{TAB_ORDER.map(tab => (
						<SubNavigation.NavigationItem
							key={tab}
							active={shell.activeTab === tab}
							onClick={() => shell.setActiveTab(tab)}
							cy={`nova-tab-${tab}`}
						>
							{TAB_LABELS[tab]}
						</SubNavigation.NavigationItem>
					))}
				</SubNavigation>
				<TabContent data-cy={`nova-tab-content-${shell.activeTab}`}>{TAB_LABELS[shell.activeTab]} tab</TabContent>
			</MainColumn>
		</Shell>
	);
}
